<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';

// Página de vendas da ChatSuite aberta com o código do embaixador: o cadastro
// fica ligado a ele e o botão de WhatsApp vai para o número que ele salvar aqui.
const props = defineProps({
  ambassador: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const whatsapp = ref(props.ambassador.whatsapp || '');
const isSaving = ref(false);

watch(
  () => props.ambassador.whatsapp,
  value => {
    whatsapp.value = value || '';
  }
);

const suggested = computed(() => {
  const phone = props.ambassador.suggested_whatsapp || '';
  return !props.ambassador.whatsapp && phone ? phone : '';
});

const metrics = computed(() => {
  const m = props.ambassador.metrics || {};
  return [
    {
      key: 'visits',
      label: t('REVENDA.AMBASSADOR_PAGE.VISITS'),
      value: m.visits ?? 0,
    },
    {
      key: 'whatsapp',
      label: t('REVENDA.AMBASSADOR_PAGE.WHATSAPP_CLICKS'),
      value: m.cta_whatsapp ?? 0,
    },
    {
      key: 'signup',
      label: t('REVENDA.AMBASSADOR_PAGE.SIGNUP_CLICKS'),
      value: m.cta_signup ?? 0,
    },
    {
      key: 'signups',
      label: t('REVENDA.AMBASSADOR_PAGE.SIGNUPS'),
      value: m.signups ?? 0,
    },
  ];
});

const copyLink = async () => {
  try {
    await copyTextToClipboard(props.ambassador.page_url);
    useAlert(t('REVENDA.REFERRAL.COPIED'));
  } catch (error) {
    useAlert(t('REVENDA.REFERRAL.COPY_ERROR'));
  }
};

const openPage = () => {
  window.open(props.ambassador.page_url, '_blank', 'noopener');
};

const save = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('reseller/updateAmbassador', whatsapp.value);
    useAlert(t('REVENDA.AMBASSADOR_PAGE.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('REVENDA.AMBASSADOR_PAGE.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-solid-2"
  >
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <span class="i-lucide-megaphone size-4 text-n-brand" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('REVENDA.AMBASSADOR_PAGE.TITLE') }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('REVENDA.AMBASSADOR_PAGE.HINT') }}
      </p>
    </div>

    <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
      <input
        :value="ambassador.page_url"
        readonly
        class="flex-1 min-w-0 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12"
        @focus="$event.target.select()"
      />
      <div class="flex gap-2">
        <Button
          icon="i-lucide-copy"
          :label="t('REVENDA.REFERRAL.COPY')"
          @click="copyLink"
        />
        <Button
          icon="i-lucide-external-link"
          variant="faded"
          color="slate"
          :label="t('REVENDA.AMBASSADOR_PAGE.OPEN')"
          @click="openPage"
        />
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-12">
        {{ t('REVENDA.AMBASSADOR_PAGE.WHATSAPP_LABEL') }}
      </label>
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          v-model="whatsapp"
          type="tel"
          :placeholder="t('REVENDA.AMBASSADOR_PAGE.WHATSAPP_PLACEHOLDER')"
          class="flex-1 min-w-0 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12"
        />
        <Button
          icon="i-lucide-check"
          :label="t('REVENDA.AMBASSADOR_PAGE.SAVE')"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
      <button
        v-if="suggested && !whatsapp"
        type="button"
        class="self-start text-xs text-n-brand hover:underline"
        @click="whatsapp = suggested"
      >
        {{ t('REVENDA.AMBASSADOR_PAGE.USE_SUGGESTED', { phone: suggested }) }}
      </button>
      <p class="text-xs text-n-slate-11">
        {{ t('REVENDA.AMBASSADOR_PAGE.WHATSAPP_HINT') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-xs text-n-slate-11">
        {{ t('REVENDA.AMBASSADOR_PAGE.METRICS') }}
      </span>
      <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div
          v-for="item in metrics"
          :key="item.key"
          class="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-n-background"
        >
          <span class="text-lg font-medium text-n-slate-12">
            {{ item.value }}
          </span>
          <span class="text-xs text-n-slate-11">{{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
